import React from 'react';
import { replacementLabels } from './utils';
import { PlayerImageChip } from '@lib/page-utils/PlayerComparison';
import { BodyRegular } from '@components/atoms/typography';

export type PlayerStatType = {
  avg: number;
  label: string;
  percent: number;
  total: string;
};

function parseTotal(stat) {
  const decimalStats = ['xG', 'xPts', 'xA'];
  if (decimalStats.includes(stat?.label)) {
    return parseFloat(stat?.total).toFixed(1);
  } else {
    return parseFloat(stat?.total).toFixed();
  }
}

const PlayerStatsTable = ({
  statsA,
  statsB,
  playersA,
  playersB
}: {
  statsA: PlayerStatType[];
  statsB: PlayerStatType[];
  playersA?: any;
  playersB?: any;
}) => {
  function bar(stat, side) {
    const width = 75 * stat?.percent + '%';
    let mClass, cClass;
    if (side === 'A') {
      cClass = 'flex-row-reverse';
      mClass = 'bg-primary rounded-bl rounded-tl';
    } else {
      cClass = '';
      mClass = 'bg-danger-600 rounded-br rounded-tr';
    }
    return (
      <div className={`my-2 flex items-center text-xs ml-auto ${cClass}`}>
        <div className={`${mClass} h-6 ${side}`} style={{ width }}></div>
        <BodyRegular className="px-2">{stat?.avg?.toFixed(2)}</BodyRegular>
      </div>
    );
  }

  const rows = () => {
    let classA = 'invisible';
    let classB = 'invisible';
    if (statsA?.length === 0 && statsB?.length === 0) {
      return;
    } else if (statsA?.length === 0) {
      statsA = statsB;
      classB = 'visible';
    } else if (statsB?.length === 0) {
      statsB = statsA;
      classA = 'visible';
    } else {
      classA = 'visible';
      classB = 'visible';
    }

    return statsA?.map((stat, index) => (
      <tr key={index}>
        <td className="text-left pl-2">
          <BodyRegular>{stat?.label}</BodyRegular>
        </td>
        <td className={classA}>{bar(stat, 'A')}</td>
        {Boolean(statsB?.length) && (
          <>
            <td className={classB}>{bar(statsB?.[index], 'B')}</td>
          </>
        )}
      </tr>
    ));
  };
  return (
    <table className="text-xs table-fixed w-full">
      <thead className="">
        <tr className="text-gray-500 text-xs">
          <th className="w-36"></th>
          <th>
            {playersA &&
              playersA.map((player, index) => (
                <div
                  className={`flex justify-end pr-2 ${
                    playersB?.length > 1 ? 'mb-2' : 'mb-1'
                  }`}
                  key={`head-to-head-${player.displayName}-${index}`}
                >
                  <PlayerImageChip player={player} />
                </div>
              ))}
          </th>
          {Boolean(statsB?.length) && (
            <>
              <th>
                {playersB &&
                  playersB.map((player, index) => (
                    <div
                      className={`flex justify-start pl-3 ${
                        playersB?.length > 1 ? 'mb-2' : 'mb-1'
                      }`}
                      key={`head-to-head-${player.displayName}-${index}`}
                    >
                      <PlayerImageChip player={player} />
                    </div>
                  ))}
              </th>
            </>
          )}
        </tr>
      </thead>
      <tbody className="text-center">{rows()}</tbody>
    </table>
  );
};

export default PlayerStatsTable;

export const PlayerHeadToHeadStatsTable = ({
  playersA,
  playersB,
  playerStatsA,
  playerStatsB
}) => (
  <table className="w-full table-auto">
    <thead>
      <tr>
        <th className="py-2"></th>
        <th className="py-2 w-32 text-left">
          {playersA.map((player, index) => (
            <div
              className={`flex justify-center ${
                playersB?.length > 1 ? 'mb-2' : 'mb-1'
              }`}
              key={`head-to-head-${player.displayName}-${index}`}
            >
              <PlayerImageChip player={player} />
            </div>
          ))}
        </th>
        <th className="py-2 w-32 text-center">
          {playersB.map((player, index) => (
            <div
              className={`flex justify-center ${
                playersB?.length > 1 ? 'mb-2' : 'mb-1'
              }`}
              key={`head-to-head-${player.displayName}-${index}`}
            >
              <PlayerImageChip player={player} />
            </div>
          ))}
        </th>
      </tr>
    </thead>
    <tbody>
      {playerStatsA.map((stat, index) => {
        return (
          <tr key={index}>
            <td className="py-2">
              {replacementLabels[stat.label] || stat.label}
            </td>
            <td className="py-2 w-32 text-center">{parseTotal(stat)}</td>
            <td className="py-2 w-32 text-center">
              {parseTotal(playerStatsB?.[index])}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);
